import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../../Firebase/Firebase.utils";

/**
 * Build the Firestore query constraints for the CMS list view.
 *
 * Two query shapes:
 *  1. Browse mode (no search): `where(statusField, '==', value)?` + `orderBy('date', 'desc')`.
 *     - Firestore needs a composite index when a status filter is applied.
 *       The first time a user applies the filter, Firestore prints a
 *       console error with a one-click index-creation URL.
 *  2. Search mode (search query is set): `where('titleLower', '>=', q)`
 *     + `where('titleLower', '<=', q + "")` + `orderBy('titleLower')`.
 *     - Status filter is intentionally NOT applied server-side here to
 *       avoid additional composite index requirements; it filters the
 *       (small) result set client-side.
 */
function buildConstraints({
  search,
  statusFilter,
  schema,
  cursor,
  pageSize,
  includeLimit,
}) {
  const constraints = [];
  const q = (search || "").trim().toLowerCase();

  if (q) {
    constraints.push(where("titleLower", ">=", q));
    constraints.push(where("titleLower", "<=", q + ""));
    constraints.push(orderBy("titleLower"));
  } else {
    if (statusFilter === "published") {
      constraints.push(where(schema.statusField, "==", schema.publishedValue));
    } else if (statusFilter === "draft") {
      constraints.push(where(schema.statusField, "==", schema.draftValue));
    }
    // Some collections use different date fields — careers uses
    // `postedAt`, blogs/news/insights use `date`. orderBy silently
    // drops docs missing the field, so this MUST match the field the
    // collection actually writes. See cmsSchema.js.
    constraints.push(orderBy(schema.orderByField || "date", "desc"));
  }

  if (cursor) constraints.push(startAfter(cursor));
  if (includeLimit) constraints.push(limit(pageSize));

  return constraints;
}

/**
 * One-shot fetch of every doc in the collection — no filter, no limit.
 * Used by the hybrid search mode in `usePaginatedDocs`: we pay once on
 * first search, then filter/paginate client-side for the rest of the
 * session. Ordered by `__name__` (always indexed) — consumers re-sort
 * client-side by `date` desc.
 */
export async function fetchAllDocs({ collectionName }) {
  const q = query(collection(db, collectionName), orderBy("__name__"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
}

/**
 * Cheap aggregate read (1 read regardless of size). Returns the total
 * number of docs that match the current search/filter combo.
 */
export async function fetchTotalCount({ collectionName, search, statusFilter, schema }) {
  const constraints = buildConstraints({
    search,
    statusFilter,
    schema,
    includeLimit: false,
  });
  const q = query(collection(db, collectionName), ...constraints);
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

/**
 * Fetch one page. Returns the data + the underlying DocumentSnapshots
 * (needed as `startAfter` cursors for subsequent pages).
 */
export async function fetchPage({
  collectionName,
  search,
  statusFilter,
  schema,
  pageSize = 12,
  cursor = null,
}) {
  const constraints = buildConstraints({
    search,
    statusFilter,
    schema,
    cursor,
    pageSize,
    includeLimit: true,
  });
  const q = query(collection(db, collectionName), ...constraints);
  const snap = await getDocs(q);
  return {
    docs: snap.docs.map((d) => ({ ...d.data(), id: d.id })),
    snaps: snap.docs,
  };
}
